import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, CheckCircle, XCircle, DollarSign, Eye, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { disputeAPI } from '../lib/api';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
  rejected: 'bg-zinc-100 text-zinc-700',
};

const STATUS_LABELS = {
  open: 'Open',
  under_review: 'Under Review',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

const REASON_LABELS = {
  driver_no_show: 'Driver no-show',
  damaged_goods: 'Damaged goods',
  late_delivery: 'Late delivery',
  wrong_items: 'Wrong items',
  unprofessional_behavior: 'Unprofessional behavior',
  safety_concern: 'Safety concern',
  billing_error: 'Billing error',
  user_no_show: 'User no-show',
  user_abusive: 'User abusive',
  other: 'Other',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const DisputesManagement = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [acting, setActing] = useState(false);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const res = await disputeAPI.adminList(statusFilter !== 'all' ? statusFilter : undefined);
      setDisputes(res.data || []);
    } catch (e) {
      devError('Failed to load disputes', e);
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDisputes(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openDetail = (d) => {
    setSelected(d);
    setAdminNotes(d.admin_notes || '');
    setRefundType('full');
    setRefundAmount(d.job_price ? d.job_price.toFixed(2) : '');
    setShowDetail(true);
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    setActing(true);
    try {
      const res = await disputeAPI.adminUpdate(selected.id, { status: newStatus, admin_notes: adminNotes });
      toast.success(`Dispute marked ${STATUS_LABELS[newStatus]}`);
      setSelected(res.data);
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update dispute');
    } finally {
      setActing(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setActing(true);
    try {
      const res = await disputeAPI.adminUpdate(selected.id, { admin_notes: adminNotes });
      toast.success('Notes saved');
      setSelected(res.data);
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save notes');
    } finally {
      setActing(false);
    }
  };

  const issueRefund = async () => {
    if (!selected) return;
    const payload = { refund_type: refundType };
    if (refundType === 'partial') {
      const amt = parseFloat(refundAmount);
      if (Number.isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
      if (amt > selected.job_price) { toast.error(`Cannot refund more than $${selected.job_price.toFixed(2)}`); return; }
      payload.amount = amt;
    }
    if (!confirm(`Issue ${refundType} refund of $${refundType === 'full' ? selected.job_price.toFixed(2) : refundAmount} back to the original Stripe payment method? This cannot be undone.`)) return;
    setActing(true);
    try {
      const res = await disputeAPI.adminRefund(selected.id, payload);
      toast.success(`Refund issued: $${res.data.refund_amount.toFixed(2)}`);
      setSelected(res.data);
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setActing(false);
    }
  };

  const counts = disputes.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="disputes-management">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-zinc-900">Disputes & Refunds</h3>
          <p className="text-xs text-zinc-500">Review user/driver disputes and issue Stripe refunds when appropriate.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">{counts.open || 0} Open</span>
          <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">{counts.under_review || 0} Reviewing</span>
          <span className="px-2 py-1 rounded-full bg-green-100 text-green-700">{counts.resolved || 0} Resolved</span>
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="bg-white border border-zinc-200">
          <TabsTrigger value="all" data-testid="dispute-filter-all">All</TabsTrigger>
          <TabsTrigger value="open" data-testid="dispute-filter-open">Open</TabsTrigger>
          <TabsTrigger value="under_review" data-testid="dispute-filter-review">Reviewing</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="dispute-filter-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="dispute-filter-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : disputes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
          <AlertTriangle className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
          <p className="text-zinc-600">No disputes in this view.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100">
          {disputes.map(d => (
            <div key={d.id} className="p-4 flex items-center justify-between gap-3 hover:bg-zinc-50" data-testid={`dispute-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[d.status] || 'bg-zinc-100 text-zinc-700'}`}>
                    {STATUS_LABELS[d.status] || d.status}
                  </span>
                  <span className="text-xs text-zinc-500 capitalize">{REASON_LABELS[d.reason] || d.reason}</span>
                  <span className="text-xs text-zinc-500">·</span>
                  <span className="text-xs text-zinc-500">${(d.job_price || 0).toFixed(2)}</span>
                  {d.refund_id && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      Refunded ${(d.refund_amount || 0).toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-zinc-900 mt-1 truncate">
                  {d.opener_name || 'Unknown'} <span className="text-zinc-400 text-xs">({d.opener_role})</span>
                </p>
                <p className="text-xs text-zinc-500 truncate">{d.description}</p>
                <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatDate(d.created_at)}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => openDetail(d)} data-testid={`view-dispute-${d.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Review
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Dispute Detail
            </DialogTitle>
            <DialogDescription>
              Review the dispute and issue a refund if warranted.
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-zinc-500 text-xs">Status</p>
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[selected.status]}`}>
                    {STATUS_LABELS[selected.status]}
                  </span>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Job amount</p>
                  <p className="font-semibold text-zinc-900">${(selected.job_price || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Opened by</p>
                  <p className="text-zinc-900">{selected.opener_name} <span className="text-xs text-zinc-500">({selected.opener_role})</span></p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Reason</p>
                  <p className="text-zinc-900">{REASON_LABELS[selected.reason] || selected.reason}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-zinc-500 text-xs">User / Driver</p>
                  <p className="text-zinc-900">{selected.user_name || '—'} ↔ {selected.driver_name || '—'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-zinc-500 text-xs">Route</p>
                  <p className="text-zinc-900 text-sm">{selected.pickup_address} → {selected.dropoff_address}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-zinc-500 text-xs">Description</p>
                  <p className="bg-zinc-50 p-3 rounded-lg text-sm text-zinc-800">{selected.description}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Admin notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Internal notes for record-keeping..."
                  rows={3}
                  data-testid="dispute-admin-notes"
                />
                <Button size="sm" variant="outline" onClick={saveNotes} disabled={acting} data-testid="save-notes-btn">
                  Save Notes
                </Button>
              </div>

              {!selected.refund_id && selected.status !== 'resolved' && selected.status !== 'rejected' && (
                <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-3">
                  <p className="text-sm font-semibold text-orange-900 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" /> Issue Refund
                  </p>
                  <div className="flex items-center gap-2">
                    <Select value={refundType} onValueChange={setRefundType}>
                      <SelectTrigger className="w-32 h-9" data-testid="refund-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Full refund</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                      </SelectContent>
                    </Select>
                    {refundType === 'partial' && (
                      <Input
                        type="number"
                        step="0.01"
                        value={refundAmount}
                        onChange={e => setRefundAmount(e.target.value)}
                        placeholder="Amount"
                        className="h-9 max-w-[120px]"
                        data-testid="refund-amount-input"
                      />
                    )}
                    <Button onClick={issueRefund} disabled={acting} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="issue-refund-btn">
                      {acting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1" />}
                      {refundType === 'full' ? `Refund $${(selected.job_price || 0).toFixed(2)}` : 'Refund Partial'}
                    </Button>
                  </div>
                  <p className="text-xs text-orange-700">Refund will be sent back to the original Stripe payment method.</p>
                </div>
              )}

              {selected.refund_id && (
                <div className="border border-green-200 bg-green-50 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-green-900">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Refund of ${(selected.refund_amount || 0).toFixed(2)} issued
                  </p>
                  <p className="text-xs text-green-700 mt-1">Refund ID: <code className="bg-white px-1 rounded">{selected.refund_id}</code></p>
                  <p className="text-xs text-green-700">Status: {selected.refund_status || 'pending'}</p>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                {selected.status === 'open' && (
                  <Button variant="outline" onClick={() => updateStatus('under_review')} disabled={acting} data-testid="mark-review-btn">
                    Mark Under Review
                  </Button>
                )}
                {selected.status !== 'resolved' && (
                  <Button variant="outline" onClick={() => updateStatus('resolved')} disabled={acting} className="text-green-700 border-green-200" data-testid="mark-resolved-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark Resolved
                  </Button>
                )}
                {selected.status !== 'rejected' && !selected.refund_id && (
                  <Button variant="outline" onClick={() => updateStatus('rejected')} disabled={acting} className="text-zinc-700 border-zinc-200" data-testid="mark-rejected-btn">
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DisputesManagement;
